import { useSyncExternalStore } from 'react';
import { type CrestThemeName, DEFAULT_CREST_THEME, normalizeCrestTheme } from './antd-theme.js';

/**
 * 界面主题个人偏好。
 *
 * 偏好保存在当前浏览器（与界面语言相同的存储方式），由 Platform 个人设置写入；
 * 门户宿主通过 MF mount/update 上下文把主题传给 Remote，Remote 未收到时回退读取本地偏好。
 * 与 Platform 仓库同名文件内容一致，修改时两边同步。
 */

export const CREST_THEME_STORAGE_KEY = 'crest_ui_theme';
export const CREST_THEME_CHANGED_EVENT = 'crest:theme-changed';

export function getStoredCrestTheme(): CrestThemeName {
  if (typeof window === 'undefined') return DEFAULT_CREST_THEME;
  try {
    return normalizeCrestTheme(window.localStorage.getItem(CREST_THEME_STORAGE_KEY));
  } catch {
    return DEFAULT_CREST_THEME;
  }
}

export function setStoredCrestTheme(theme: CrestThemeName) {
  if (typeof window === 'undefined') return;
  const next = normalizeCrestTheme(theme);
  try {
    window.localStorage.setItem(CREST_THEME_STORAGE_KEY, next);
  } catch {
    // 隐私模式或存储配额不足时仅本次会话生效。
  }
  applyCrestThemeToElement(next);
  window.dispatchEvent(new CustomEvent(CREST_THEME_CHANGED_EVENT, { detail: next }));
}

/** 写入 data-crest-theme，供主题 CSS 变量（styles/theme-tokens.css）选择色板。 */
export function applyCrestThemeToElement(
  theme: CrestThemeName,
  element: HTMLElement | undefined = typeof document === 'undefined'
    ? undefined
    : document.documentElement,
) {
  if (element) element.dataset.crestTheme = normalizeCrestTheme(theme);
}

export function subscribeCrestTheme(listener: () => void) {
  if (typeof window === 'undefined') return () => undefined;
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === CREST_THEME_STORAGE_KEY) listener();
  };
  window.addEventListener(CREST_THEME_CHANGED_EVENT, listener);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(CREST_THEME_CHANGED_EVENT, listener);
    window.removeEventListener('storage', onStorage);
  };
}

/** 当前生效主题；宿主显式传入时以宿主为准，否则跟随本地偏好。 */
export function useCrestTheme(hostTheme?: CrestThemeName | null): CrestThemeName {
  const stored = useSyncExternalStore(
    subscribeCrestTheme,
    getStoredCrestTheme,
    () => DEFAULT_CREST_THEME,
  );
  return hostTheme ? normalizeCrestTheme(hostTheme) : stored;
}
